
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { WKT } from 'ol/format';
import { LineString } from 'ol/geom';

import { useSessionStore } from 'session/useSessionStore';
import { CacheSystem } from 'features/seismic/models/enums/CacheSystem';
import { useGetLineGeometryImperatively, useGetZSliceTile } from '../api/useSeismic3DInfoController';
import { DimensionIndex, Dimension3D } from './Dimension3D';
import { LineType } from '../models/enums/LineType';
import { Metadata3D } from '../models/types/Metadata3D';
import { Survey3DInfo } from '../models/types/Survey3DInfo';
import { ZSliceTileRequest } from '../models/types/ZSliceTileRequest';
import { use3DGridStore } from '../stores/use3DGridStore';
import { use3DSceneStore } from '../stores/use3DSceneStore';
import { getUrlParams } from '../utils/Seismic3DUrlUtils';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';

export type Zslice3DProps = {
    surveyMetadata: Metadata3D,
    sliceNumbersToLoad: number[],
    onTotalSlicesLoadedChange: (total: number) => void,
    colorbar: string,
    isVisible: boolean,
    onLoadedSlicesChange: (val: number[]) => void,
    setSelectedZSliceNumber: (val: number) => void,
    setIsSearchedZSliceLoading: (val: boolean) => void,
    searchedZSliceNumber: number|null,
    zsliceNumbersToLoad: number[],
    srid: number,
};

/*
    const zsliceNumbers = use3DGridStore(state => state.totalZsliceNumbers);
    const setZsliceNumbers = use3DGridStore(state => state.setTotalZsliceNumbers);

    const searchedZsliceNumber = use3DGridStore(state => state.searchedZsliceNumber);
    const setSelectedZsliceNumber = use3DGridStore(state => state.setSelectedZSliceNumber);
    const setIsZsliceSearchLoading= use3DGridStore(state => state.setIsZsliceSearchLoading);
*/

export const Zslice3D = ({
    surveyMetadata,
    sliceNumbersToLoad,
    onTotalSlicesLoadedChange,
    colorbar,
    isVisible,
    onLoadedSlicesChange,
    searchedZSliceNumber,
    setIsSearchedZSliceLoading,
    setSelectedZSliceNumber,
    zsliceNumbersToLoad,
    srid,
} : Zslice3DProps) => {
    const totalLoadedSlicesRef = useRef(1);

    const { mutateAsync: getLineGeometry } = useGetLineGeometryImperatively();

    const [ loadedZsliceNumbers, setLoadedZSliceNumbers ] = useState<number[]>([]);

    const survey3DInfo = surveyMetadata.Survey3DInfo;
    const surveyHeader = surveyMetadata.Header;
    const entitlementLimits = surveyMetadata.Entitlement3DLimits;

    const geopostScene = use3DSceneStore(state => state.scene);

    const [ xlineGeomReference, inlineGeomReference ] = use3DGridStore(state => [state.xlineGeomReference, state.inlineGeomReference]);

    const { current: createdDepthNumbers } = useRef<number[]>([]);

    const [tileHeight, tileWidth] = [350, 350];

    const tracesXPixelsFactor = useMemo(() => {
        const divisionFactor = 6000;
        const factor = Math.round((survey3DInfo.TotalInlines + survey3DInfo.TotalXlines) / divisionFactor);
        return factor > 0 ? factor : 1;
    }, [survey3DInfo]);

    const [ tracesTileWidth, tracesTileHeight ] = useMemo(() => {
        return [ tileWidth * tracesXPixelsFactor, tileHeight * tracesXPixelsFactor];
    }, [tileWidth, tileHeight, tracesXPixelsFactor]);

    const [ samplesPerTrace, sampleInterval, byteSize ] = [
        surveyHeader?.SamplesPerTrace ?? 0,
        surveyHeader?.SampleInterval ?? 0,
        surveyHeader?.ByteSize ?? 0
    ];

    const [ inlineStart, inlineEnd, inlineIncrement ] = useMemo(() => [survey3DInfo.InlineStart, survey3DInfo.InlineEnd, survey3DInfo.InlineIncrement], [survey3DInfo.InlineEnd, survey3DInfo.InlineStart, survey3DInfo.InlineIncrement]);
    const [ xlineStart, xlineEnd, xlineIncrement ] = useMemo(() => [survey3DInfo.XlineStart, survey3DInfo.XlineEnd, survey3DInfo.XlineIncrement], [survey3DInfo.XlineEnd, survey3DInfo.XlineStart, survey3DInfo.XlineIncrement]);

    const [ priorityDimensionIndex, setPriorityDimensionIndex ] = useState<DimensionIndex | null>(null);
    const [ dimensionIndexesToLoad, setDimensionIndexesToLoad ] = useState<DimensionIndex[]>([]);

    const [ selectedNumber, setSelectedNumber ] = useState<number | null>(null);

    const { mutateAsync: getZsliceTile } = useGetZSliceTile(SurveyType.Seismic3D);

    const seismic3DDefaults = useSessionStore(state => state.tenantConfiguration?.viewer3DDefaults);

    const prepareRenderChunkSize = useSessionStore(state => state.tenantConfiguration?.prepareRenderChunkSize);

    const loadTile = async (depthNumber: number, tileInlineStart: number, tileInlineEnd: number, tileXlineStart: number, tileXlineEnd: number) => {

        const params: ZSliceTileRequest = {
            sampleInterval,
            byteSize,
            surveyInlineStart: survey3DInfo.InlineStart,
            surveyInlineEnd: survey3DInfo.InlineEnd,
            surveyInlineIncrement: survey3DInfo.InlineIncrement,
            surveyXlineStart: survey3DInfo.XlineStart,
            surveyXlineEnd: survey3DInfo.XlineEnd,
            surveyXlineIncrement: survey3DInfo.XlineIncrement,
            minSampleValue: surveyMetadata.MinSampleValue,
            maxSampleValue: surveyMetadata.MaxSampleValue,
            applyWatermark: false,
            colorbar: colorbar,
            dpi: 1,
            filePath: surveyMetadata.VolumePath,
            volumeToken: surveyMetadata.VolumeToken,
            forceDisableCache: getUrlParams().forceDisableCache,
            forceDisableSamplesCache: false,
            imageCacheSystem: CacheSystem.CloudStorage,
            inverted: false,
            jpgQuality: 70,
            samplesCacheSystem: CacheSystem.CloudStorage,
            parallelProcesses: 10,
            skipTraceCount: getUrlParams().traceDecimation,
            tileHeight,
            tileWidth,
            render: getUrlParams().sourceApi,
            traceRangeEnd: 0,
            traceRangeStart:0,
            inlineStart: tileInlineStart * inlineIncrement + entitlementLimits.InlineStart,
            inlineEnd: tileInlineEnd * inlineIncrement + entitlementLimits.InlineStart,
            xlineStart: tileXlineStart * xlineIncrement + entitlementLimits.XlineStart,
            xlineEnd: tileXlineEnd * xlineIncrement + entitlementLimits.XlineStart,
            depthNumber,
            isIndexedVolume: true,
            inlineDimensionName: surveyMetadata.DimensionNames.Inline,
            xlineDimensionName: surveyMetadata.DimensionNames.Xline,
            sampleDimensionName: surveyMetadata.DimensionNames.Sample,
            cdpDimensionName: surveyMetadata.DimensionNames.Cdp,
            verticallyInverted: false,
            prepareRenderChunkSize: (prepareRenderChunkSize ?? 128),
            antialias: false,
            sourceApi: getUrlParams().sourceApi,
        };

        return await getZsliceTile(params);
    };

    const setHeightScale = use3DSceneStore(state => state.setHeightScale);

    const onLoadTile = useCallback(
        (tile: Blob, lineNumber: number, tileInlineStart: number, tileLineEnd: number, tileXlineStart: number, tileSampleEnd: number) =>
            geopostScene?.addTileTexture(surveyMetadata.VolumeToken, tileInlineStart, tileXlineStart, lineNumber, LineType.ZSlice, tile)
        , [geopostScene]
    );

    const onLoadSlice = useCallback((sliceNumber: number) => {
        if (sliceNumber === searchedZSliceNumber) {
            setIsSearchedZSliceLoading(false);
        }
        totalLoadedSlicesRef.current += 1;
        console.log('TOTAL LOADED SLICES ==========>', totalLoadedSlicesRef);
        onTotalSlicesLoadedChange(totalLoadedSlicesRef.current);
    }, [onTotalSlicesLoadedChange, searchedZSliceNumber, setIsSearchedZSliceLoading]);

    const totalInlines = useMemo(() => entitlementLimits.TotalInlines, [entitlementLimits.TotalInlines]);
    const totalXlines = useMemo(() => entitlementLimits.TotalXlines, [entitlementLimits.TotalXlines]);

    useEffect(() => {
        if (!!xlineGeomReference && !!inlineGeomReference) {
            const normalizeToOneDigit = (num: number) => {
                if (num === 0) { return 0 }

                const absNum = Math.abs(num);
                const exponent = Math.floor(Math.log10(absNum));
                const normalized = num / Math.pow(10, exponent);

                return normalized;
            };

            const xlineGeom = (new WKT()).readGeometry(xlineGeomReference.Line, {
                dataProjection: 'EPSG:3857'
            });
            const inlineGeom = (new WKT()).readGeometry(inlineGeomReference.Line, {
                dataProjection: 'EPSG:3857'
            });

            const xlineGeomLength = (xlineGeom.transform('EPSG:' + srid, 'EPSG:4326') as LineString).getLength();
            const inlineGeomLength = (inlineGeom.transform('EPSG:' + srid, 'EPSG:4326') as LineString).getLength();
            const xlineTracesLength = (xlineGeomReference.Max - xlineGeomReference.Min) / surveyMetadata.Survey3DInfo.InlineIncrement;
            const inlineTracesLength = (inlineGeomReference.Max - inlineGeomReference.Min) / surveyMetadata.Survey3DInfo.XlineIncrement;
            const xlineScale = parseFloat(normalizeToOneDigit(xlineGeomLength / xlineTracesLength).toFixed(2));
            const inlineScale = parseFloat(normalizeToOneDigit(inlineGeomLength / inlineTracesLength).toFixed(2));
            geopostScene?.scale.setX(xlineScale);
            geopostScene?.scale.setZ(inlineScale);
            setHeightScale(Math.min(xlineScale, inlineScale));
        }
    }, [inlineGeomReference, xlineGeomReference]);

    useEffect(() => {
        const newDepthNumbersToLoad = sliceNumbersToLoad.filter(depthNumber => !createdDepthNumbers.includes(depthNumber));
        createdDepthNumbers.push(...newDepthNumbersToLoad);
        newDepthNumbersToLoad.forEach(depthNumber => {
            geopostScene?.addZSliceTilesByDepth(surveyMetadata.VolumeToken, totalInlines,totalXlines, (entitlementLimits.InlineStart - inlineStart) / inlineIncrement, (entitlementLimits.XlineStart - xlineStart) / xlineIncrement, depthNumber, sampleInterval, tracesTileWidth, tracesTileHeight);
        });

        setLoadedZSliceNumbers(current => [...current, ...newDepthNumbersToLoad]);

        setDimensionIndexesToLoad(sliceNumbersToLoad.map(depthNumber => ({
            indexNumber: depthNumber,
            totalX: totalInlines,
            totalY: totalXlines
        })));
        //onAddTiles();
    }, [sliceNumbersToLoad]);

    useEffect(() => {
        if (!!searchedZSliceNumber || searchedZSliceNumber === 0) {
            if (!dimensionIndexesToLoad.find(x => x.indexNumber === searchedZSliceNumber) && priorityDimensionIndex?.indexNumber !== searchedZSliceNumber && !sliceNumbersToLoad.includes(searchedZSliceNumber)) {
                setIsSearchedZSliceLoading(true);
                geopostScene?.addZSliceTilesByDepth(surveyMetadata.VolumeToken, totalInlines, totalXlines, (entitlementLimits.InlineStart - inlineStart) / inlineIncrement, (entitlementLimits.XlineStart - xlineStart) / xlineIncrement, searchedZSliceNumber, sampleInterval, tracesTileWidth, tracesTileHeight);
                setPriorityDimensionIndex({ indexNumber: searchedZSliceNumber, totalX: totalInlines, totalY: totalXlines });

                setLoadedZSliceNumbers(current => {
                    const newZSliceNumbers = [...current, searchedZSliceNumber];
                    newZSliceNumbers.sort((a, b) => a - b);
                    return newZSliceNumbers;
                });
            }
            setSelectedNumber(searchedZSliceNumber);
        }
    }, [searchedZSliceNumber, geopostScene]);

    useEffect(() => {
        onLoadedSlicesChange(loadedZsliceNumbers);
    }, [loadedZsliceNumbers]);

    return (
        <Dimension3D
            onIndexLoading={() => {}}
            isVisible = { isVisible }
            volumeToken={surveyMetadata.VolumeToken}
            indexesToLoad={dimensionIndexesToLoad}
            lineType={LineType.ZSlice}
            loadTile={loadTile}
            onLoadIndex={onLoadSlice}
            onLoadTile={onLoadTile}
            onSelectIndex={setSelectedZSliceNumber}
            selectedIndexNumber={selectedNumber}
            priorityIndexToLoad={priorityDimensionIndex}
            tileHeight={tracesTileHeight}
            tileWidth={tracesTileWidth}
        />
    );
};